"use client"
import React, { useState } from "react";
import Topbar from "@/components/UI/navbar/topbar";
import Footer from "@/components/UI/footer/footer";
import { SEOUtil } from "@/utils/SEOUtil";
import {
    Download,
    X,
    CheckCircle2,
    XCircle,
    FileImage,
    Image as ImageIcon,
    FileText,
    Info
} from "lucide-react";
import ContentElement from "@/components/UI/ContentElement/ContentElement";

const PressCenter: React.FC = () => {
    const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);

    const handleDownload = (fileName: string) => {
        const link = document.createElement("a");
        link.href = `https://cdn.szkolnyprzewodnik.edu.pl/download/materialy-prasowe/${fileName}`;
        link.download = fileName; 
        link.rel = "noopener noreferrer";
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    return (
        <>
            <SEOUtil
                name="Centrum Prasowe"
                url="/press-center"
                description="Pobierz logotypy, banery i materiały informacyjne."
                mainEntityOfPage
                page
            />
            <Topbar />

            <main className="min-h-screen text-white5 pb-32">

                <header className="relative w-full bg-main h-[60vh] flex flex-col justify-center items-center text-center px-6 overflow-hidden">
                    <div className="absolute inset-0   z-10" />
                    <div className="absolute inset-0 z-0 opacity-10 bg-[url('/ankieta/form.webp')] bg-cover bg-center"  />

                    <div className="relative z-20 max-w-5xl">
                        <h1 className="text-white16 font-black tracking-tighter mb-8 text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.1] drop-shadow-2xl">
                            Centrum Prasowe
                        </h1>
                        <p className="text-white48 text-xl md:text-3xl font-medium max-w-3xl mx-auto leading-relaxed">
                            Oficjalne logotypy i informacje o projekcie <br/> <span className="text-btn">Szkolny Przewodnik 2.0</span>
                        </p>
                    </div>
                </header>

                <section className="mx-auto max-w-480  max-lg:max-w-5xl max-xl:max-w-7xl max-2xl:max-w-384 w-full flex justify-center mt-20 ">
                    <div className="bg-sectionbg rounded-[2.5rem] shadow-custom p-10 md:p-16 mb-20 border-t-8 border-btn max-w-360">
                        <div className="flex items-center gap-6 mb-10">
                            <Info className="w-12 h-12 text-btn" />
                            <h2 className="text-4xl md:text-5xl font-bold text-white16">O Projekcie</h2>
                        </div>
                        <div className="prose prose-xl prose-invert max-w-none text-justify leading-loose text-white48">
                            <p>
                                Szkolny Przewodnik 2.0 to projekt stworzony z myślą o uczniach, którzy mają trudności z wyborem dalszej ścieżki edukacyjnej i zawodowej, szczególnie uczniach klas ósmych, którzy często nie mają dostępu do rzetelnych informacji o szkołach i oferowanych przez nie kierunkach. Dlatego przeciwstawiamy się tym problemom, oferując wsparcie w postaci narzędzi pomagających zrozumieć różne ścieżki rozwoju, dopasować je do zainteresowań i umiejętności oraz planować swoją edukację. Młodzież często boryka się z trudnościami w nauce i brakiem skutecznych metod rozwijania kluczowych kompetencji, co wpływa na spadek motywacji i pewności siebie. Naszą misją jest wspieranie świadomych decyzji dotyczących wyboru szkół i zawodów, dostarczając rzetelne informacje, narzędzia i praktyczne wsparcie. Wierzymy, że wczesne wsparcie buduje solidne podstawy pod budowanie przyszłej kariery i wzmacnia pewność siebie.
                            </p>
                        </div>
                    </div>
                </section>

                <ContentElement heading={"Pisownia i Nazewnictwo"} subheading={"Zasady poprawnego użycia nazwy projektu w mediach, publikacjach i materiałach promocyjnych."} className="mb-24 select-none bg-sectionbg py-30" isWhite >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                        <div className="bg-main border-2 border-green-500/80 shadow-green-500/40 rounded-3xl p-10 flex items-start gap-6 transition-transform hover:scale-[1.01] shadow-lg">
                            <CheckCircle2 className="w-12 h-12 text-green-500 flex-shrink-0 mt-2" />
                            <div>
                                <h4 className="font-bold text-green-400 text-2xl mb-4">Poprawnie</h4>
                                <p className="text-white5 font-bold text-3xl md:text-4xl tracking-wide">Szkolny Przewodnik 2.0</p>
                                <p className="text-lg text-white48 mt-4">Używaj pełnej nazwy z zachowaniem wielkich liter.</p>
                            </div>
                        </div>

                        <div className="bg-sectionbg border-2 border-red-500/80 shadow-red-500/40 rounded-3xl p-10 flex items-start gap-6 transition-transform hover:scale-[1.01] shadow-lg">
                            <XCircle className="w-12 h-12 text-red-500 flex-shrink-0 mt-2" />
                            <div>
                                <h4 className="font-bold text-red-500 text-2xl mb-4">Niepoprawnie</h4>
                                <ul className="space-y-3">
                                    <li className="text-red-500 font-bold text-2xl md:text-3xl">· szkolny przewodnik</li>
                                    <li className="text-red-500 font-bold text-2xl md:text-3xl">· Szkolny Przewodnik</li>
                                    <li className="text-red-500 font-bold text-2xl md:text-3xl">· SP</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </ContentElement>


                <ContentElement heading="Zasoby Graficzne" subheading="Oficjalne materiały wizualne do pobrania i wykorzystania w mediach oraz publikacjach." className="text-center mt-10 mb-20 py-10">

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10 text-left">

                        <div className="group bg-sectionbg rounded-3xl shadow-custom overflow-hidden flex flex-col hover:-translate-y-2 transition-transform duration-short cursor-pointer">
                            <div className="h-64 bg-main50 flex items-center justify-center relative overflow-hidden">
                                <div className="absolute inset-0 bg-btn opacity-0 group-hover:opacity-10 transition-opacity duration-short" />
                                <FileImage className="w-24 h-24 text-icons group-hover:scale-110 transition-transform duration-short" />
                            </div>
                            <div className="p-10 flex flex-col flex-grow">
                                <h4 className="text-3xl font-bold text-white16 mb-4">Logotyp Projektu</h4>
                                <p className="text-white48 text-lg mb-8 flex-grow">
                                    Oficjalne logo w wysokiej rozdzielczości. Dostępne warianty z tłem i bez.
                                </p>
                                <button
                                    onClick={() => setIsLogoModalOpen(true)}
                                    className="w-full flex items-center justify-center gap-3 bg-btn text-white5 text-xl font-bold py-5 px-6 rounded-2xl hover:brightness-110 transition-all active:scale-95 shadow-lg shadow-blue-900/20 cursor-pointer"
                                >
                                    <Download className="w-7 h-7" />
                                    Pobierz Logo
                                </button>
                            </div>
                        </div>

                        <div className="group bg-sectionbg rounded-3xl shadow-custom overflow-hidden flex flex-col hover:-translate-y-2 transition-transform duration-short cursor-pointer">
                            <div className="h-64 bg-main50 flex items-center justify-center relative overflow-hidden">
                                <div className="absolute inset-0 bg-btn opacity-0 group-hover:opacity-10 transition-opacity duration-short" />
                                <FileText className="w-24 h-24 text-icons group-hover:scale-110 transition-transform duration-short" />
                            </div>
                            <div className="p-10 flex flex-col flex-grow">
                                <h4 className="text-3xl font-bold text-white16 mb-4">Plakat Promocyjny</h4>
                                <p className="text-white48 text-lg mb-8 flex-grow">
                                    Plakat formatu A3 gotowy do druku. Idealny do szkół.
                                </p>
                                <button
                                    onClick={() => handleDownload('plakat.pdf')}
                                    className="w-full flex items-center justify-center gap-3 bg-btn text-white5 text-xl font-bold py-5 px-6 rounded-2xl hover:brightness-110 transition-all active:scale-95 shadow-lg shadow-blue-900/20 cursor-pointer"
                                >
                                    <Download className="w-7 h-7" />
                                    Pobierz Plakat
                                </button>
                            </div>
                        </div>

                        <div className="group bg-sectionbg rounded-3xl shadow-custom overflow-hidden flex flex-col hover:-translate-y-2 transition-transform duration-short cursor-pointer">
                            <div className="h-64 bg-main50 flex items-center justify-center relative overflow-hidden">
                                <div className="absolute inset-0 bg-btn opacity-0 group-hover:opacity-10 transition-opacity duration-short" />
                                <ImageIcon className="w-24 h-24 text-icons group-hover:scale-110 transition-transform duration-short" />
                            </div>
                            <div className="p-10 flex flex-col flex-grow">
                                <h4 className="text-3xl font-bold text-white16 mb-4">Baner Projektu</h4>
                                <p className="text-white48 text-lg mb-8 flex-grow">
                                    Grafika w formacie poziomym do wykorzystania w social mediach.
                                </p>
                                <button
                                    onClick={() => handleDownload('baner.png')}
                                    className="w-full flex items-center justify-center gap-3 bg-btn text-white5 text-xl font-bold py-5 px-6 rounded-2xl hover:brightness-110 transition-all active:scale-95 shadow-lg shadow-blue-900/20 cursor-pointer"
                                >
                                    <Download className="w-7 h-7" />
                                    Pobierz Baner
                                </button>
                            </div>
                        </div>

                    </div>
                </ContentElement>
            </main>

            <Footer />

            {isLogoModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
                    <div className="bg-sectionbg w-full max-w-lg rounded-3xl shadow-custom overflow-hidden animate-in zoom-in-95 duration-200 relative border border-white48/10">

                        <div className="bg-main50 p-8 border-b border-white48/10 flex justify-between items-center">
                            <h3 className="text-2xl font-bold text-white5">Wybierz wersję logo</h3>
                            <button
                                onClick={() => setIsLogoModalOpen(false)}
                                className="p-3 bg-sectionbg rounded-full hover:bg-main transition-colors text-white48 hover:text-white5"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-8 space-y-6">
                            <button
                                onClick={() => handleDownload('logo-transparent.png')}
                                className="w-full group flex items-center gap-6 p-4 rounded-2xl border-2 border-white48/20 hover:border-btn hover:bg-main50 transition-all text-left cursor-pointer"
                            >
                                <div className="w-20 h-20 shrink-0 rounded-xl bg-[url('https://www.transparenttextures.com/patterns/checkerboard.png')] bg-repeat flex items-center justify-center border border-gray-600 overflow-hidden bg-white">
                                    <img
                                        src={`https://cdn.szkolnyprzewodnik.edu.pl/images/materialy-prasowe/logo-transparent.png`}
                                        alt="Logo Przezroczyste"
                                        className="w-full h-full object-contain p-2"
                                    />
                                </div>
                                <div>
                                    <span className="block text-xl font-bold text-white5 group-hover:text-btn transition-colors">Wersja przezroczysta</span>
                                    <span className="text-base text-white48">Format PNG, bez tła</span>
                                </div>
                                <Download className="w-8 h-8 text-white48 ml-auto group-hover:text-btn" />
                            </button>

                            <button
                                onClick={() => handleDownload('logo-background.png')}
                                className="w-full group flex items-center gap-6 p-4 rounded-2xl border-2 border-white48/20 hover:border-btn hover:bg-main50 transition-all text-left cursor-pointer"
                            >
                                <div className="w-20 h-20 shrink-0 rounded-xl bg-white flex items-center justify-center border border-gray-600 overflow-hidden">
                                    <img
                                        src={`https://cdn.szkolnyprzewodnik.edu.pl/images/materialy-prasowe/logo-background.png`}
                                        alt="Logo z tłem"
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <div>
                                    <span className="block text-xl font-bold text-white5 group-hover:text-btn transition-colors">Wersja z tłem</span>
                                    <span className="text-base text-white48">Format PNG, niebieskie tło</span>
                                </div>
                                <Download className="w-8 h-8 text-white48 ml-auto group-hover:text-btn" />
                            </button>
                        </div>

                        <div className="p-4 bg-main50 text-center text-sm text-white48 font-medium">
                            Kliknięcie rozpocznie pobieranie wybranego pliku.
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PressCenter;

